/**
 * Retention UI tiplari — `GET /api/v1/retention/runs` javobiga mos
 * (4-bosqich kontrakt §6.4).
 *
 * ⚠️ **MINIMAL kontrakt.** Server qo'shimcha maydonlar qaytarishi
 * mumkin va parser ulardan yiqilmasligi kerak — mobil kontrakt §10
 * qoidasi panel uchun ham amal qiladi.
 */

export const RETENTION_KINDS = ["location_points", "checkin_photos", "orphan_objects"] as const;
export type RetentionKind = (typeof RETENTION_KINDS)[number];

export const RETENTION_STATUSES = ["dry_run", "confirmed", "running", "done", "failed"] as const;
export type RetentionStatus = (typeof RETENTION_STATUSES)[number];

/**
 * `retention_runs.details` — shakl TURGA QARAB farq qiladi:
 * `location_points` da `partitions[]`, `checkin_photos` va
 * `orphan_objects` da `namuna[]`. Ikkalasida ham `bytes`.
 *
 * ⚠️ Har maydon ixtiyoriy: `details` jsonb va eski yugurishlarda
 * boshqacha bo'lishi mumkin.
 */
export interface RetentionDetails {
	partitions?: string[];
	namuna?: string[];
	bytes?: number;
	[key: string]: unknown;
}

export interface RetentionRun {
	id: string;
	kind: RetentionKind;
	status: RetentionStatus;
	cutoffAt: string;
	candidateCount: number;
	deletedCount: number;
	bytesFreed: number;
	details: RetentionDetails | null;
	error: string | null;
	requestedBy: string | null;
	confirmedBy: string | null;
	reason: string | null;
	createdAt: string;
	confirmedAt: string | null;
	startedAt: string | null;
	finishedAt: string | null;
}

/** OCHIQ holatlar — kartalar bo'limida ko'rsatiladi. */
export const OCHIQ_HOLATLAR: RetentionStatus[] = ["dry_run", "confirmed", "running"];

/**
 * i18next `t` funksiyasining soddalashtirilgan tipi — bu fayl React
 * komponenti EMAS, shuning uchun `useTranslation()` ni o'zi chaqira
 * olmaydi; chaqiruvchi komponent `t` ni parametr sifatida uzatadi.
 */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * ⚠️ Matnlar `@skud/core` dagi `messages` da EMAS — frontend unga
 * bog'lanmaydi (CLAUDE.md import chegaralari). P7 i18n'dan boshlab
 * ular ham SHU YERDA yozilmaydi, balki `retention.json`dagi
 * `kinds.*`/`status.*` kalitlaridan olinadi — enum qiymati (`location_points`,
 * `dry_run`, ...) AYNAN i18n kalit segmenti bilan bir xil.
 */
export function kindLabel(t: Translate, kind: string): string {
	return t(`kinds.${kind}`, { defaultValue: kind });
}

export function statusLabel(t: Translate, status: string): string {
	return t(`status.${status}`, { defaultValue: status });
}

/**
 * Holat rangi.
 *
 * ⚠️⚠️ **`dry_run` — OGOHLANTIRISH (sariq), XAVF (qizil) EMAS.**
 * Bu hali HECH NARSA o'chirilmagan holat; qizil «allaqachon kech»
 * degan taassurot berardi va admin shoshib qaror qabul qilardi.
 */
export const STATUS_RANGI: Record<RetentionStatus, string> = {
	// biome-ignore lint/style/useNamingConvention: enum qiymati DB ustuni bilan bir xil
	dry_run: "warning",
	confirmed: "processing",
	running: "processing",
	done: "success",
	failed: "default",
};

/**
 * Baytlarni odam o'qiydigan shaklga keltiradi.
 *
 * ⚠️ **1024 asosi** (KiB/MiB/GiB) — MinIO va `pg_total_relation_size`
 * ikkalasi ham xom baytda o'lchaydi, ya'ni 1000 asosi bilan
 * ko'rsatilgan hajm operatorning `du` chiqishidan farq qilardi.
 */
export function baytFormat(bytes: number | null | undefined): string {
	if (!bytes || bytes <= 0) {
		return "0 B";
	}

	const birliklar = ["B", "KiB", "MiB", "GiB", "TiB"];
	let qiymat = bytes;
	let daraja = 0;

	while (qiymat >= 1024 && daraja < birliklar.length - 1) {
		qiymat /= 1024;
		daraja += 1;
	}

	return `${daraja === 0 ? qiymat : qiymat.toFixed(1)} ${birliklar[daraja]}`;
}

/**
 * Yugurish nimani o'chirishini bir qatorda aytadi — modal va kartada
 * AYNAN bir xil matn ishlatiladi.
 *
 * ⚠️ Hajm `details.bytes` dan olinadi: `bytes_freed` faqat BAJARILGAN
 * yugurishda to'ladi, dry-run da esa u 0. Admin tasdiqlashdan oldin
 * aynan HISOBLANGAN hajmni ko'rishi kerak.
 */
export function nomzodXulosasi(t: Translate, run: RetentionRun): string {
	const hajm = baytFormat(run.details?.bytes ?? run.bytesFreed);

	if (run.kind === "location_points") {
		return t("candidateSummary.locationPoints", { count: run.candidateCount, size: hajm });
	}

	if (run.kind === "orphan_objects") {
		return t("candidateSummary.orphanObjects", { count: run.candidateCount, size: hajm });
	}

	return t("candidateSummary.photos", { count: run.candidateCount, size: hajm });
}
